import * as service from '../services/customerService';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';
import { ChildCustomerDetails, ChildPracticeMetadata, CustomerMetadata, LoadingState } from '../types';

export interface ChildCustomerState extends ChildCustomerDetails {
  balance: number,
  customerMetadata: CustomerMetadata | null,
  loadingState: LoadingState,
}

const initialState: ChildCustomerState = {
  balance: 0,
  customer_number: '',
  customerService: [],
  doctor_name: '',
  email: '',
  is_taxable: false,
  name: '',
  phone: '',
  fax: '',
  city: '',
  state: '',
  street: '',
  street2: '',
  zip: '',
  is_bill_to_parent: false,
  teamMembers: [],
  corporate_parent: false,
  customerMetadata: null,
  loadingState: LoadingState.NotStarted,
};

export const getChildBalance = createAsyncThunk('setChildBalance', service.getBalance)
export const getChildCustomerService = createAsyncThunk('setChildCustomerService', service.getCustomerService)
export const getChildTeamMembers = createAsyncThunk('setChildTeamMembers', service.getTeamMembers)
export const getChildCustomerMetadata = createAsyncThunk('setChildCustomerMetadata', service.getCustomerMetadata)

export const getChildCustomerData = createAsyncThunk(
  'getChildCustomerData',
  async (customerNumber: string, { dispatch }) => {
    await Promise.all([
      dispatch(getChildBalance(customerNumber)),
      dispatch(getChildTeamMembers(customerNumber)),
      dispatch(getChildCustomerService(customerNumber)),
      dispatch(getChildCustomerMetadata(customerNumber))
    ])
  }
)

export const childCustomerSlice = createSlice({
  name: 'childCustomer',
  initialState,
  reducers: {
    setChildCustomer: (state, action: PayloadAction<ChildPracticeMetadata>) => {
      const child = action.payload

      state.customer_number = child.customer_number
      state.doctor_name = child.doctor_name
      state.is_taxable = child.is_taxable
      state.name = child.name
      state.corporate_parent = child.corporate_parent
      state.street = child.street ?? ''
      state.street2 = child.street2 ?? ''
      state.city = child.city ?? ''
      state.state = child.state ?? ''
      state.zip = child.zip ?? ''
      state.email = child.email ?? ''
      state.phone = child.phone ?? ''
      state.fax = child.fax ?? ''
    },
    clearChildCustomer: () => {
      return initialState;
    }
  },
  extraReducers(builder) {
    builder.addCase(getChildBalance.fulfilled, (state, action) => {
      state.balance = action.payload
    })

    builder.addCase(getChildCustomerService.fulfilled, (state, action) => {
      state.customerService = action.payload
    })

    builder.addCase(getChildTeamMembers.fulfilled, (state, action) => {
      if (!state) return
      state.teamMembers = action.payload ?? []
    })

    builder.addCase(getChildCustomerMetadata.fulfilled, (state, action) => {
      if (!state) return
      state.customerMetadata = action.payload
    })

    builder.addMatcher(isPending(getChildCustomerData), (state) => {
      state.loadingState = LoadingState.Loading
    })

    builder.addMatcher(isRejected(getChildCustomerData), (state) => {
      state.loadingState = LoadingState.Error
    })

    builder.addMatcher(isFulfilled(getChildCustomerData), (state) => {
      state.loadingState = LoadingState.Done
    })
  }
});

export const { setChildCustomer, clearChildCustomer } = childCustomerSlice.actions;

export const selectChildBalance = (state: RootState) => state.childCustomer.balance

export const selectChildCustomerNumber = (state: RootState) => state.childCustomer.customer_number;

export const selectChildHasDE = (state: RootState) => state.childCustomer.customerService?.some((service) => service.serviceType === 'DE') ?? false;

export const selectChildTeamMembers = (state: RootState) => state.childCustomer.teamMembers;

export const selectIsChildCorporateParent = (state: RootState) => state.childCustomer.corporate_parent

export const selectChildCustomer = (state: RootState) => state.childCustomer;

export const selectChildCustomerLoadingState = (state: RootState) => state.childCustomer.loadingState

export const selectChildCustomerMetadata = (state: RootState) => state.childCustomer.customerMetadata

export default childCustomerSlice.reducer;
